import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import UploadPortal from "./pages/UploadPortal";
import OSDashboardNy from "./OSDashboardNy";
import OSInfoskaerm from "./OSInfoskaerm";
import OSTeamNoter from "./OSTeamNoter";
import FiltjekOprydning from "./components/FiltjekOprydning";
import KlientPortal from "./KlientPortal";
import SvHjaelp from "./SvHjaelp";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from './api';
import { T, BG, ThemeProvider, ThemeToggle } from './tema';
import NyKlient from './components/NyKlient';
import { KlientOversigt, Toast, beregn_deadline_info } from './components/KlientOversigt';
import BrugerProfil from './BrugerProfil';
import { toast } from './utils';
import {
 MobilBottomNav, ErrorBoundary, SubBoundary,
 HjaelpManualVisning, sagstype_label, Select,
} from './components/Ui';
import {
 ToastContainer, LOGO_SRC,
 DashboardVisning, MailTelefonKoePopup, KommendeMoederOverblik,
 AdminKontrolpanel, SessionTimeoutAdvarsel, ImportFraOneDrive,
} from './components/Admin';
import { LoginSkærm } from './components/Login';
import { ImportWizard } from './components/Notifikationer';
import { SøgVisning } from './components/Søgning';
import { AmbientOrbs, MouseTilt3D } from './components/AppEffects';
import { InfoskaermV2, DanmarkskortVisning } from './components/Infoskaerm';
import { useDashboard } from './hooks/useDashboard';
import { STATUS_FARVE2, deadline_kant_farve, er_kritisk } from './utils/sagHelpers';
import AndroidOpdateringsBanner from './components/AndroidOpdateringsBanner';
import KursusForaeldreansvarsloven from './components/KursusForaeldreansvarsloven';
import KursusBarnetslov from './components/KursusBarnetslov';
import KursusHub from './components/KursusHub';

// ── Upload Afventer-visning (inline, sagsbehandler-only) ──────────────────────
function UploadAfventerView({ onTilbage, bruger }) {
  const [filer, setFiler] = React.useState([]);
  const [lader, setLader] = React.useState(true);
  const [godkend_dialog, setGodkendDialog] = React.useState(null);
  const [afvis_dialog, setAfvisDialog] = React.useState(null);

  React.useEffect(() => {
    fetch('/api/upload-portal/afventer', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { filer: [] })
      .then(d => { setFiler(d.filer || []); setLader(false); })
      .catch(() => setLader(false));
  }, []);

  async function godkend(fil_id, filnavn, beskrivelse) {
    await fetch(`/api/upload-portal/fil/${fil_id}/godkend`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filnavn, beskrivelse }),
    });
    setFiler(prev => prev.filter(f => f.id !== fil_id));
    setGodkendDialog(null);
  }

  async function afvis(fil_id, grund) {
    await fetch(`/api/upload-portal/fil/${fil_id}/afvis`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grund }),
    });
    setFiler(prev => prev.filter(f => f.id !== fil_id));
    setAfvisDialog(null);
  }

  const grupperet = React.useMemo(() => {
    const map = {};
    filer.forEach(f => {
      const key = f.sag_id;
      if (!map[key]) map[key] = { sagsnummer: f.sagsnummer, klient_navn: f.klient_navn, filer: [] };
      map[key].filer.push(f);
    });
    return Object.values(map);
  }, [filer]);

  const T2 = { turkis: '#3fd4c7', creme: '#e2e8f0', dæmpet: '#64748b' };
  const panel = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16 };

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px) clamp(16px,4vw,40px)', maxWidth: 900, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={onTilbage} style={{ background: 'none', border: 'none', color: T2.dæmpet, cursor: 'pointer', fontSize: 22, padding: 0 }}>←</button>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: T2.creme }}>📥 Afventer godkendelse</h1>
          <div style={{ fontSize: 13, color: T2.dæmpet, marginTop: 2 }}>Klientuploads der venter på gennemgang</div>
        </div>
      </div>
      {lader ? (
        <div style={{ textAlign: 'center', color: T2.dæmpet, padding: 40 }}>Henter…</div>
      ) : grupperet.length === 0 ? (
        <div style={{ ...panel, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ color: T2.creme, fontSize: 15, fontWeight: 600 }}>Ingen afventende uploads</div>
          <div style={{ color: T2.dæmpet, fontSize: 13, marginTop: 6 }}>Alle klientdokumenter er gennemgået</div>
        </div>
      ) : grupperet.map(gruppe => (
        <div key={gruppe.sagsnummer} style={{ ...panel, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T2.turkis }}>{gruppe.sagsnummer}</span>
            <span style={{ fontSize: 13, color: T2.creme }}>{gruppe.klient_navn}</span>
            <span style={{ fontSize: 11, color: T2.dæmpet, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '2px 8px' }}>{gruppe.filer.length} fil{gruppe.filer.length !== 1 ? 'er' : ''}</span>
          </div>
          {gruppe.filer.map(f => (
            <div key={f.id} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T2.creme, marginBottom: 3 }}>
                  📄 {f.upload_navn || f.original_navn}
                </div>
                {f.upload_noter && <div style={{ fontSize: 12, color: T2.dæmpet, marginBottom: 3, fontStyle: 'italic' }}>"{f.upload_noter}"</div>}
                <div style={{ fontSize: 11, color: T2.dæmpet }}>
                  {f.uploadet_dato ? new Date(f.uploadet_dato).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' }) : ''} ·
                  {f.størrelse ? ` ${(f.størrelse / 1024 / 1024).toFixed(1)} MB` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => setGodkendDialog({ id: f.id, filnavn: f.upload_navn || f.original_navn, beskrivelse: '' })}
                  style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 7, color: '#34d399', fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>✓ Godkend</button>
                <button onClick={() => setAfvisDialog({ id: f.id, grund: '' })}
                  style={{ background: 'rgba(251,113,133,0.12)', border: '1px solid rgba(251,113,133,0.25)', borderRadius: 7, color: '#fb7185', fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>✗ Afvis</button>
              </div>
            </div>
          ))}
        </div>
      ))}
      {/* Godkend-dialog */}
      {godkend_dialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setGodkendDialog(null)}>
          <div style={{ background: '#0d1117', border: '1px solid rgba(63,212,199,0.2)', borderRadius: 16, padding: 28, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', color: T2.creme, fontSize: 16 }}>✓ Godkend fil</h3>
            <label style={{ fontSize: 12, color: T2.dæmpet, display: 'block', marginBottom: 4 }}>Filnavn</label>
            <input style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 10px', color: T2.creme, fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}
              value={godkend_dialog.filnavn} onChange={e => setGodkendDialog(d => ({ ...d, filnavn: e.target.value }))} />
            <label style={{ fontSize: 12, color: T2.dæmpet, display: 'block', marginBottom: 4 }}>Beskrivelse (valgfrit)</label>
            <textarea style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 10px', color: T2.creme, fontSize: 13, minHeight: 60, resize: 'vertical', boxSizing: 'border-box' }}
              value={godkend_dialog.beskrivelse} onChange={e => setGodkendDialog(d => ({ ...d, beskrivelse: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setGodkendDialog(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: T2.dæmpet, fontSize: 13, padding: '8px 16px', cursor: 'pointer' }}>Annuller</button>
              <button onClick={() => godkend(godkend_dialog.id, godkend_dialog.filnavn, godkend_dialog.beskrivelse)}
                style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 8, color: '#34d399', fontSize: 13, fontWeight: 700, padding: '8px 20px', cursor: 'pointer' }}>Godkend</button>
            </div>
          </div>
        </div>
      )}
      {/* Afvis-dialog */}
      {afvis_dialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setAfvisDialog(null)}>
          <div style={{ background: '#0d1117', border: '1px solid rgba(251,113,133,0.2)', borderRadius: 16, padding: 28, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', color: T2.creme, fontSize: 16 }}>✗ Afvis fil</h3>
            <label style={{ fontSize: 12, color: T2.dæmpet, display: 'block', marginBottom: 4 }}>Grund (valgfrit)</label>
            <textarea style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 10px', color: T2.creme, fontSize: 13, minHeight: 80, resize: 'vertical', boxSizing: 'border-box' }}
              value={afvis_dialog.grund} onChange={e => setAfvisDialog(d => ({ ...d, grund: e.target.value }))} placeholder="Fx: Forkert filtype, mangler underskrift…" />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setAfvisDialog(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: T2.dæmpet, fontSize: 13, padding: '8px 16px', cursor: 'pointer' }}>Annuller</button>
              <button onClick={() => afvis(afvis_dialog.id, afvis_dialog.grund)}
                style={{ background: 'rgba(251,113,133,0.12)', border: '1px solid rgba(251,113,133,0.25)', borderRadius: 8, color: '#fb7185', fontSize: 13, fontWeight: 700, padding: '8px 20px', cursor: 'pointer' }}>Afvis</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* SkilsmisseVejen v3 — Glasmorfisme + Fri trin-navigation + Indstillinger */
export default function App() {
 // Service worker registreres i src/index.js (standard CRA-mønster) —
 // ikke her, for at undgå dobbelt-registrering.
 return (
 <ErrorBoundary> <ThemeProvider> <AppIndre /> </ThemeProvider> </ErrorBoundary>
 );
}
function AppIndre() {
 const {
 visning, setVisning, naviger,
 aktiv_filter, setAktivFilter,
 sag_visning, setSagVisning,
 import_aktiv, setImportAktiv,
 server_version,
 nyVersionBanner, setNyVersionBanner,
 sag_sortering, setSagSortering,
 recentlyViewed,
 aktivitet_data,
 mine_sager, setMineSager,
 total_sager,
 åbn_sag_id, setÅbnSagId,
 maal_haendelse_id, setMaalHaendelseId,
 nye_haendelser_liste, fjern_nye_haendelse,
 dashboard_deadline_dialog, setDashboardDeadlineDialog,
 bruger, setBruger,
 hent_mine_sager,
 kør_import,
 log_ud,
 } = useDashboard();
 // ── NOTIFIKATIONS-BADGE STATE ─────────────────────────────────────────
 const [ulæste_antal, setUlæsteAntal] = React.useState(0);
 const [notif_sag_ids, setNotifSagIds] = React.useState(new Set());
 const [vis_notif_panel, setVisNotifPanel] = React.useState(false);
 const [notif_liste, setNotifListe] = React.useState([]);
 const hent_notif_antal = React.useCallback(() => {
   if (!bruger) return;
   fetch('/api/notifikationer/antal', { credentials: 'include' })
     .then(r => r.ok ? r.json() : null)
     .then(d => {
       if (!d) return;
       setUlæsteAntal(d.antal || 0);
       setNotifSagIds(new Set(d.sag_ids || []));
     })
     .catch(() => {});
 }, [bruger]);
 React.useEffect(() => {
   hent_notif_antal();
   const interval = setInterval(hent_notif_antal, 30000);
   return () => clearInterval(interval);
 }, [hent_notif_antal]);
 const hent_notif_liste = React.useCallback(() => {
   fetch('/api/notifikationer', { credentials: 'include' })
     .then(r => r.ok ? r.json() : null)
     .then(d => { if (d) setNotifListe(d.notifikationer || []); })
     .catch(() => {});
 }, []);
 React.useEffect(() => {
   if (vis_notif_panel) hent_notif_liste();
 }, [vis_notif_panel, hent_notif_liste]);
 const marker_laest = React.useCallback((db_id) => {
   fetch(`/api/notifikationer/${db_id}/laest`, { method: 'PUT', credentials: 'include' })
     .then(() => { hent_notif_antal(); hent_notif_liste(); })
     .catch(() => {});
 }, [hent_notif_antal, hent_notif_liste]);
 const marker_alle_laest = React.useCallback(() => {
   fetch('/api/notifikationer/laes-alle', { method: 'PUT', credentials: 'include' })
     .then(() => { hent_notif_antal(); hent_notif_liste(); })
     .catch(() => {});
 }, [hent_notif_antal, hent_notif_liste]);
 React.useEffect(() => {
   if (!vis_notif_panel) return;
   const luk = () => setVisNotifPanel(false);
   document.addEventListener('click', luk);
   return () => document.removeEventListener('click', luk);
 }, [vis_notif_panel]);
 // ── AL DATA — placeret FØR early returns (React Rules of Hooks) ──────
 const bruger_navn = bruger?.navn || "";
 const PRIMÆRE_SAGER = useMemo(() => mine_sager.filter(s => s.primær === bruger_navn).sort((a,b) => b.risiko - a.risiko), [mine_sager, bruger_navn]);
 const SEKUNDÆRE_SAGER = useMemo(() => mine_sager.filter(s => s.sekundær === bruger_navn && s.primær !== bruger_navn).sort((a,b) => b.risiko - a.risiko), [mine_sager, bruger_navn]);
 const MINE_SAGER = useMemo(() => [...PRIMÆRE_SAGER, ...SEKUNDÆRE_SAGER], [PRIMÆRE_SAGER, SEKUNDÆRE_SAGER]);
 const DEADLINES_UGE = useMemo(() => { const om7 = new Date(); om7.setDate(om7.getDate() + 7); om7.setHours(23,59,59,999); return mine_sager.filter(s => s.deadline && new Date(s.deadline) <= om7).sort((a,b) => new Date(a.deadline) - new Date(b.deadline)); }, [mine_sager]);
 // René 5/10: "jeg skulle ikke se noget omkring anja da jeg ikke er hendes
 // sagsbehandler" -- skal filtreres til EGNE sager (MINE_SAGER), ikke alle
 // sager på kontoret (mine_sager, på trods af navnet). Styrer "Se alle →"-
 // knappens synlighed og den udvidede liste ved aktiv_filter==="nye_haendelser".
 const NYE_HAENDELSER_SAGER = useMemo(() => MINE_SAGER.filter(s => s.har_nye_haendelser).sort((a,b) => (a.klient||"").localeCompare(b.klient||"", "da")), [MINE_SAGER]);
 // KPI tæl — bruger mine_sager (backend filtrerer allerede efter rolle:
 // admin/direktør ser alle, sagsbehandler kun egne). Sikrer at direktør
 // ser ALLE kritiske, ikke kun sager hvor de er primær/sekundær.
 const kritiske = useMemo(() => {
 const today = new Date(); today.setHours(0,0,0,0);
 return mine_sager.filter(s=>{
 if (!s.deadline) return false;
 const d = new Date(s.deadline); d.setHours(0,0,0,0);
 return d < today;
 });
 }, [mine_sager]);
 const dl_i_dag = useMemo(() => {
 const today = new Date(); today.setHours(0,0,0,0);
 return mine_sager.filter(s=>{
 if (!s.deadline) return false;
 const d = new Date(s.deadline); d.setHours(0,0,0,0);
 return d.getTime() === today.getTime();
 });
 }, [mine_sager]);
 const dl_alle = useMemo(() => mine_sager.filter(s=>s.deadline), [mine_sager]);
 // Aktiv filtrering — standard: mine sager. alle_kontor: alle sager på kontoret
 const filtrerede_sager = useMemo(() => {
 if (aktiv_filter === "alle_kontor") return [...mine_sager].sort((a,b)=>b.risiko-a.risiko);
 if (aktiv_filter === "kritisk") return kritiske;
 if (aktiv_filter === "deadline") return dl_i_dag;
 if (aktiv_filter === "aktiv") return MINE_SAGER.filter(s=>s.status==="aktiv");
 if (aktiv_filter === "opgaver") return dl_alle;
 if (aktiv_filter === "nye_haendelser") return NYE_HAENDELSER_SAGER;
 return MINE_SAGER;
 }, [aktiv_filter, mine_sager, kritiske, dl_i_dag, dl_alle, NYE_HAENDELSER_SAGER, MINE_SAGER]);
 // ── Upload-portal routing — klienter uploades via /upload/:token uden login
 if (window.location.pathname.startsWith('/upload/')) {
   const _upload_token = window.location.pathname.replace(/^\/upload\//, '').trim();
   if (_upload_token) return <UploadPortal token={_upload_token} />;
 }
 // ── Klient-portal routing — tilgås via /portal?noegle=xxx uden login
 const _portal_noegle = new URLSearchParams(window.location.search).get("noegle");
 if (_portal_noegle) return <KlientPortal noegle={_portal_noegle} />;
 if (window.location.pathname === '/os-infoskaerm') return <OSInfoskaerm />;
 // ── Vis login-skærm hvis ikke logget ind ──
 if (!bruger) return <><ToastContainer /><AmbientOrbs /><MouseTilt3D /><LoginSkærm onLogin={b => setBruger(b)} /></>;
 // ── Infoskærm-rolle ser kun den nye V2 ──
 if (bruger.rolle === "infoskærm" || bruger.rolle === "infoskaerm") return (
 <SubBoundary navn="Infoskærm V2"><InfoskaermV2 onTilbage={log_ud} /></SubBoundary>
 );
 // Infoskærm-visning (V2 ved direkte navigation)
 if (visning==="infoskærm" || visning==="infoskaerm") return (
 <SubBoundary navn="Infoskærm V2"><InfoskaermV2 onTilbage={()=>naviger("menu")} /></SubBoundary>
 );
 // H6 fix: Teamnotater renderes nu inline med sidebar (fjernet early return)
 // ── VISNING META — titel per side ──
 const VISNING_META = {
 menu: { titel:"Overblik", ikon:"" },
 dashboard: { titel:"Dashboard", ikon:"" },
 klienter: { titel:"Sager", ikon:"" },
 "ny-klient": { titel:"Ny klient", ikon:"" },
 "import": { titel:"Importér sag", ikon:"" },
 "import-onedrive": { titel:"Import fra OneDrive", ikon:"" },
 søg: { titel:"Søgning", ikon:"" },
 geo: { titel:"Geografisk overblik", ikon:"" },
 "hjaelp": { titel:"Hjælp", ikon:"" },
 "min-profil": { titel:"Min profil", ikon:"" },
 indstillinger: { titel:"Administration", ikon:"" },
 kurser: { titel:"Kurser", ikon:"🎓" },
 "kurser-foraeldreansvarsloven": { titel:"Forældreansvarsloven", ikon:"📚" },
 "kurser-barnetslov": { titel:"Barnets lov", ikon:"⚖️" },
 };
 const side_meta = VISNING_META[visning] || VISNING_META.menu;
 const filter_titler = { kritisk:"Kritiske sager", deadline:"Deadlines i dag", aktiv:"Aktive sager", opgaver:"Åbne opgaver", nye_haendelser:"Sager med nye hændelser" };
 return (
 <> <ToastContainer />
 {nyVersionBanner && (
 <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:9990,
 background:'linear-gradient(90deg, #2322be 0%, #4140d4 100%)',
 color:'#fff', padding:'11px 20px',
 display:'flex', alignItems:'center', justifyContent:'space-between',
 fontSize:14, boxShadow:'0 -4px 24px rgba(35,34,190,0.5)',
 borderTop:'1px solid rgba(255,255,255,0.15)' }}> <span> En ny version er tilgængelig</span> <div style={{ display:'flex', gap:10, alignItems:'center' }}> <button onClick={() => window.location.reload()} style={{
 background:'#fff', color:'#2322be',
 border:'none', borderRadius:7, padding:'6px 18px',
 fontWeight:700, cursor:'pointer', fontSize:13
 }}>Opdater nu</button> <button onClick={() => setNyVersionBanner(false)} style={{
 background:'none', border:'none', color:'rgba(255,255,255,0.7)',
 cursor:'pointer', fontSize:20, lineHeight:1, padding:'0 4px'
 }}>×</button> </div> </div>
 )}
 <AndroidOpdateringsBanner />
 <SessionTimeoutAdvarsel bruger={bruger} onLogout={log_ud} /> <MailTelefonKoePopup bruger={bruger} /> <MobilBottomNav visning={visning} naviger={naviger} log_ud={log_ud} bruger={bruger} />
 {/* Import baggrunds-statusbar */}
 {import_aktiv && visning !== "import" && (
 <div style={{ position:"fixed", bottom: nyVersionBanner ? 56 : 20, right:20, zIndex:10001, background:"rgba(10,14,46,0.97)", border:T.kort_kant, borderRadius:12, padding:"14px 18px", boxShadow:"0 8px 32px rgba(0,0,0,0.5)", minWidth:280, animation:"fadeUp 0.3s ease", transition:"bottom 0.2s ease" }}> <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
 {import_aktiv.progress < 100 && !import_aktiv.fejl && (
 <div style={{ width:16, height:16, border:"2px solid "+T.turkis, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }} />
 )}
 {import_aktiv.progress >= 100 && <span style={{ fontSize:16 }}></span>}
 {import_aktiv.fejl && <span style={{ fontSize:16 }}></span>}
 <div> <div style={{ fontSize:13, color:T.creme, fontWeight:500 }}>Import: {import_aktiv.klient}</div> <div style={{ fontSize:12, color: import_aktiv.fejl ? "#ff6b6b" : T.turkis }}>{import_aktiv.status_tekst}</div> </div> </div> <div style={{ height:3, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}> <div style={{ width:import_aktiv.progress+"%", height:"100%", background: import_aktiv.fejl ? "#ff6b6b" : import_aktiv.progress>=100 ? "#69db7c" : T.turkis, borderRadius:2, transition:"width 0.5s ease" }} /> </div> </div>
 )}
 {/* Dashboard deadline dialog */}
 {dashboard_deadline_dialog && (() => {
 const d = dashboard_deadline_dialog;
 const er_ændring = !!d.eksisterende; // har allerede en deadline
 const kan_gem = er_ændring
 ? !!(d.ny_titel?.trim() && d.ny_deadline && d.begrundelse?.trim())
 : !!(d.ny_titel?.trim() && d.ny_deadline);
 const input_stil = { width:"100%", background:"rgba(255,255,255,0.06)", border:T.skillelinje, borderRadius:8, padding:"10px 12px", color:"#e2e8f0", fontSize:14, fontFamily:"'Inter',system-ui,sans-serif", boxSizing:"border-box", marginBottom:12 };
 return (
 <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(8px)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}
 onClick={()=>setDashboardDeadlineDialog(null)}> <div style={{ background:"#0f1328", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:"clamp(16px, 4vw, 28px) clamp(16px, 5vw, 32px)", width:420, maxWidth:"92vw", boxSizing:"border-box", boxShadow:"0 24px 80px rgba(0,0,0,0.6)" }}
 onClick={e=>e.stopPropagation()}> <div style={{ fontSize:16, fontWeight:600, color:T.turkis, marginBottom:20 }}>{er_ændring ? "Ændr deadline" : "Sæt deadline"}</div>
 {er_ændring && d.original_titel && (
 <div style={{ padding:"8px 12px", background:"rgba(255,255,255,0.04)", borderRadius:8, marginBottom:14, fontSize:12 }}> <span style={{ color:"#94a3b8" }}>Nuværende: </span> <span style={{ color:"#f1f5f9", fontWeight:500 }}>{d.original_titel}</span>
 {d.original_deadline && <span style={{ color:"#94a3b8" }}> — {d.original_deadline}</span>}
 </div>
 )}
 <div style={{ fontSize:12, color:"#b0bec5", marginBottom:6 }}>Hvad omhandler deadlinen? <span style={{ color:"#fb7185" }}>*</span></div> <input value={d.ny_titel||""} onChange={e=>setDashboardDeadlineDialog(p=>({...p, ny_titel:e.target.value}))}
 placeholder="fx: Partshøringsfrist, Svar til Familieretshuset, Indsendelse af dokumentation..."
 spellCheck="true" lang="da" autoFocus
 style={{ ...input_stil, border:`1px solid ${d.ny_titel?.trim() ? "rgba(255,255,255,0.12)" : "rgba(251,113,133,0.3)"}` }} /> <div style={{ fontSize:12, color:"#b0bec5", marginBottom:6 }}>Dato <span style={{ color:"#fb7185" }}>*</span></div> <input type="date" value={d.ny_deadline} onChange={e=>setDashboardDeadlineDialog(p=>({...p, ny_deadline:e.target.value}))}
 style={{ ...input_stil, colorScheme:"dark" }} />
 {er_ændring && (
 <> <div style={{ fontSize:12, color:"#b0bec5", marginBottom:6 }}>Begrundelse for ændring <span style={{ color:"#fb7185" }}>*</span></div> <input value={d.begrundelse||""} onChange={e=>setDashboardDeadlineDialog(p=>({...p, begrundelse:e.target.value}))}
 placeholder="fx: Aftalt med klient, Familieretshuset udsat fristen..."
 spellCheck="true" lang="da"
 style={input_stil} /> </>
 )}
 <div style={{ display:"flex", gap:10, marginTop:4 }}> <button onClick={()=>setDashboardDeadlineDialog(null)} style={{ flex:1, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"10px", color:"#b0bec5", fontSize:13, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}>Fortryd</button> <button disabled={!kan_gem} onClick={async()=>{
 if (!kan_gem) return;
 const ny = d.ny_deadline;
 const ny_titel = d.ny_titel?.trim() || "";
 const ny_info = beregn_deadline_info(ny);
 const orig_sager = mine_sager;
 setMineSager(p=>p.map(s=>s.id===d.sag_id?{...s,deadline:ny,deadline_titel:ny_titel,deadline_txt:ny_info.txt,deadline_farve:ny_info.farve}:s));
 setDashboardDeadlineDialog(null);
 toast(er_ændring ? "Deadline ændret" : "Deadline sat", "success");
 api("sager/" + d.sag_id, "PATCH", { næste_deadline: ny, næste_deadline_titel: ny_titel }).catch(err => {
 console.error(err);
 setMineSager(orig_sager);
 toast("Deadline blev ikke gemt — prøv igen", "fejl");
 });
 }}
 style={{ flex:1, background:kan_gem ? "linear-gradient(135deg, #3fd4c7 0%, #2ca89f 100%)" : "rgba(63,212,199,0.2)", border:"none", borderRadius:8, padding:"10px", color:kan_gem?"#04150f":"#475569", fontSize:13, fontWeight:700, cursor:kan_gem?"pointer":"not-allowed", fontFamily:"'Inter',system-ui,sans-serif", transition:"all 0.2s", boxShadow:kan_gem?"0 4px 14px rgba(63,212,199,0.25)":"none" }}>
 {er_ændring ? "Gem ændring" : "Sæt deadline"}
 </button> </div>
 {/* Markér som udført — kun synlig ved eksisterende deadline */}
 {er_ændring && (<>
 <div style={{ borderTop:"1px solid rgba(255,255,255,0.1)", margin:"16px 0" }} />
 <div>
 <label style={{ fontSize:12, color:"rgba(255,255,255,0.6)", marginBottom:6, display:"block" }}>Beskriv hvad der skete (valgfri)</label>
 <textarea value={d.udfoert_beskrivelse||""} onChange={e=>setDashboardDeadlineDialog(p=>({...p, udfoert_beskrivelse:e.target.value}))} placeholder="fx: Svar sendt til Familieretshuset, Aktindsigt modtaget og gennemgået..." rows={2} spellCheck="true" lang="da" style={{ width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:6, color:"#fff", padding:"8px", fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"'Inter',system-ui,sans-serif" }} />
 <button onClick={async()=>{
 try {
 await api("sager/" + d.sag_id, "PATCH", { næste_deadline: null, næste_deadline_titel: null });
 if ((d.udfoert_beskrivelse||"").trim()) {
 await api("haendelser", "POST", { sag_id: d.sag_id, type:"deadline_udfoert", titel:"Deadline udfort: " + (d.original_titel || d.ny_titel || "Deadline"), beskrivelse: d.udfoert_beskrivelse.trim(), dato: new Date().toISOString() }).catch(err => { console.warn("[DEADLINE] note fejlede:", err); });
 }
 setMineSager(p=>p.map(s=>s.id===d.sag_id?{...s,deadline:null,deadline_titel:"",deadline_txt:"—",deadline_farve:"#94a3b8"}:s));
 toast("Deadline markeret som udfort", "success");
 } catch(err) { console.error(err); toast("En handling fejlede — prøv igen", "fejl"); }
 setDashboardDeadlineDialog(null);
 }} style={{ marginTop:8, width:"100%", padding:"10px", background:"#16a34a", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:"'Inter',system-ui,sans-serif" }}>
 Markér som udfort
 </button>
 </div>
 </>)}
 </div> </div>
 );
 })()}
 <div style={{ minHeight:"100vh", background:BG, display:"flex", position:"relative" }}> <AmbientOrbs /> <MouseTilt3D />
 {/* ═══ SIDEBAR NAVIGATION — skjules på mobil portrait ═══ */}
 <nav className="sv-sidebar-desktop" style={{
 width:76, minHeight:"100vh",
 background:"linear-gradient(180deg, rgba(5,8,24,0.99) 0%, rgba(8,11,28,0.98) 100%)",
 borderRight:"1px solid rgba(255,255,255,0.06)",
 display:"flex", flexDirection:"column", alignItems:"center", paddingTop:20, gap:4,
 position:"fixed", left:0, top:0, zIndex:100,
 boxShadow:"4px 0 40px rgba(0,0,0,0.5), 1px 0 0 rgba(255,255,255,0.03)",
 backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
 }}> <div className="sv-sidebar-glow" />
 {/* Logo */}
 <div onClick={()=>{setVisning("menu");setAktivFilter(null);}} style={{ cursor:"pointer", marginBottom:24, padding:8 }}> <img src={LOGO_SRC} alt="SV" style={{ width:40, height:40, borderRadius:12, objectFit:"contain" }} /> </div>
 {/* Nav items */}
 {[
 { id:"menu", ikon:"", label:"Overblik" },
 { id:"dashboard", ikon:"", label:"Dashboard" },
 { id:"klienter", ikon:"", label:"Sager" },
 { id:"ny-klient", ikon:"", label:"Ny klient" },
 { id:"import", ikon:"", label:"Import" },
 { id:"import-onedrive", ikon:"", label:"OneDrive" },
 { id:"søg", ikon:"", label:"Søg" },
 { id:"upload-godkend", ikon:"📥", label:"Uploads" },
 { id:"geo", ikon:"", label:"Kort" },
 { id:"teamnoter", ikon:"", label:"Notater" },
 { id:"infoskærm", ikon:"", label:"Skærm" },
 { id:"kurser", ikon:"🎓", label:"Kurser" },
 ].map(item => {
 const aktiv = visning === item.id || (item.id === "menu" && visning === "menu");
 return (
 <div key={item.id}
 role="button"
 tabIndex={0}
 onClick={() => item.id === "menu" ? (setVisning("menu"), setAktivFilter(null)) : naviger(item.id)}
 onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), item.id === "menu" ? (setVisning("menu"), setAktivFilter(null)) : naviger(item.id))}
 title={item.label}
 aria-label={item.label}
 aria-current={aktiv ? "page" : undefined}
 className={aktiv ? "sv-sidebar-aktiv-bg" : undefined}
 style={{
 position:"relative",
 width:60, height:54, borderRadius:14,
 display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
 cursor:"pointer",
 transition:"all 0.22s cubic-bezier(0.34,1.56,0.64,1)",
 background: aktiv
 ? "linear-gradient(135deg, rgba(63,212,199,0.18) 0%, rgba(79,70,229,0.12) 100%)"
 : "transparent",
 border: aktiv ? "1px solid rgba(63,212,199,0.25)" : "1px solid transparent",
 margin:"1px 0",
 boxShadow: aktiv ? "0 4px 20px rgba(63,212,199,0.12), inset 0 1px 0 rgba(255,255,255,0.05)" : "none",
 transform: aktiv ? "translateX(2px)" : "none",
 }}
 onMouseOver={e=>{ if(!aktiv) { e.currentTarget.style.background="rgba(255,255,255,0.06)"; e.currentTarget.style.transform="translateX(2px)"; }}}
 onMouseOut={e=>{ if(!aktiv) { e.currentTarget.style.background="transparent"; e.currentTarget.style.transform="none"; }}}>
 {/* Active indicator bar + glow bloom */}
 {aktiv && <> <div style={{ position:"absolute", left:0, top:"15%", bottom:"15%", width:3, background:"linear-gradient(180deg,transparent,#3fd4c7,transparent)", borderRadius:"0 3px 3px 0", boxShadow:"0 0 12px #3fd4c7, 0 0 24px rgba(63,212,199,0.4)" }} /> <div style={{ position:"absolute", inset:0, borderRadius:14, background:"radial-gradient(ellipse at 20% 50%, rgba(63,212,199,0.08) 0%, transparent 70%)", pointerEvents:"none" }} /> </>}
 <span aria-hidden="true" style={{ fontSize:21, lineHeight:1, filter: aktiv ? "drop-shadow(0 0 8px rgba(63,212,199,0.8)) drop-shadow(0 0 16px rgba(63,212,199,0.4))" : "none", transition:"filter 0.2s" }}>{item.ikon}</span> <span style={{ fontSize:10, color: aktiv ? "#3fd4c7" : "#94a3b8", marginTop:3, fontFamily:"'Inter',system-ui,sans-serif", fontWeight: aktiv ? 700 : 400, letterSpacing:aktiv?"0.04em":0, transition:"color 0.2s" }}>{item.label}</span> </div>
 );
 })}
 {/* Spacer */}
 <div style={{ flex:1 }} />
 {/* OS Dashboard — kun synlig for os_admin */}
 {bruger?.os_admin && (() => {
 const os_aktiv = visning === "os";
 return (
 <div role="button" tabIndex={0} onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),naviger("os"))} onClick={()=>naviger("os")} title="OS Dashboard"
 style={{
 width:56, height:52, borderRadius:12, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
 cursor:"pointer", transition:"all 0.2s",
 background: os_aktiv ? "rgba(63,212,199,0.12)" : "transparent",
 borderLeft: os_aktiv ? "3px solid #3fd4c7" : "3px solid transparent",
 marginBottom:4,
 }}
 onMouseOver={e=>{ if(!os_aktiv) e.currentTarget.style.background="rgba(255,255,255,0.05)" }}
 onMouseOut={e=>{ if(!os_aktiv) e.currentTarget.style.background="transparent" }}> <span style={{ fontSize:22 }}></span> <span style={{ fontSize:11, color: os_aktiv ? "#3fd4c7" : "#cbd5e1", marginTop:4, fontFamily:"'Inter',system-ui,sans-serif", fontWeight: os_aktiv ? 600 : 400 }}>OS</span> </div>
 );
 })()}
 {/* Admin */}
 {(() => {
 const admin_aktiv = visning === "indstillinger";
 return (
 <div role="button" tabIndex={0} onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),naviger("indstillinger"))} onClick={()=>naviger("indstillinger")} title="Indstillinger"
 style={{
 width:56, height:52, borderRadius:12, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
 cursor:"pointer", transition:"all 0.2s",
 background: admin_aktiv ? "rgba(63,212,199,0.12)" : "transparent",
 borderLeft: admin_aktiv ? "3px solid #3fd4c7" : "3px solid transparent",
 marginBottom:4,
 }}
 onMouseOver={e=>{ if(!admin_aktiv) e.currentTarget.style.background="rgba(255,255,255,0.05)" }}
 onMouseOut={e=>{ if(!admin_aktiv) e.currentTarget.style.background="transparent" }}> <span style={{ fontSize:22 }}></span> <span style={{ fontSize:11, color: admin_aktiv ? "#3fd4c7" : "#cbd5e1", marginTop:4, fontFamily:"'Inter',system-ui,sans-serif", fontWeight: admin_aktiv ? 600 : 400 }}>Indstillinger</span> </div>
 );
 })()}
 {/* Hjælp */}
 <div role="button" tabIndex={0} onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),setVisning("hjaelp"))} onClick={()=>setVisning("hjaelp")} title="Hjælp"
 style={{
 width:56, height:52, borderRadius:12, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
 cursor:"pointer", transition:"all 0.2s",
 background: visning==="hjaelp" ? "rgba(63,212,199,0.12)" : "transparent",
 borderLeft: visning==="hjaelp" ? "3px solid #3fd4c7" : "3px solid transparent",
 marginBottom:4,
 }}
 onMouseOver={e=>{ if(visning!=="hjaelp") e.currentTarget.style.background="rgba(255,255,255,0.05)" }}
 onMouseOut={e=>{ if(visning!=="hjaelp") e.currentTarget.style.background="transparent" }}> <span style={{ fontSize:22, opacity:0.7 }}>?</span> <span style={{ fontSize:12, color: visning==="hjaelp" ? "#3fd4c7" : "#cbd5e1", marginTop:4, fontFamily:"'Inter',system-ui,sans-serif", fontWeight: visning==="hjaelp" ? 600 : 400 }}>Hjælp</span> </div>
 {/* Profil */}
 {/* K5-fix: sv-dk-profil-avatar → CSS-override hæver border-opacity til 0.60 i mørkt tema */}
 <div role="button" tabIndex={0} onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),setVisning("min-profil"))} onClick={()=>setVisning("min-profil")} title={bruger?.navn || "Min profil"}
 className="sv-dk-profil-avatar"
 style={{
 width:48, height:48, borderRadius:14, marginBottom:16, marginTop:8, cursor:"pointer",
 background:"rgba(63,212,199,0.15)", border:"1px solid rgba(63,212,199,0.25)",
 display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
 transition:"all 0.2s",
 }}
 onMouseOver={e=>{e.currentTarget.style.background="rgba(63,212,199,0.25)";e.currentTarget.style.transform="scale(1.05)"}}
 onMouseOut={e=>{e.currentTarget.style.background="rgba(63,212,199,0.15)";e.currentTarget.style.transform="scale(1)"}}> <span className="sv-dk-profil-initialer" style={{ fontSize:15, fontWeight:700, color:"#3fd4c7", lineHeight:1 }}>{bruger?.initialer || "RS"}</span> <span className="sv-dk-profil-label" style={{ fontSize:12, color:"#b0bec5", marginTop:2 }}>Profil</span> </div>
 {/* V1.0: Version-badge nederst i sidebar */}
 <div title={`SkilsmisseVejen Sagsportal v${server_version}`}
 style={{ fontSize:10, color:"#64748b", letterSpacing:"0.05em", marginBottom:10, fontFamily:"'Inter',system-ui,sans-serif", opacity:0.7 }}>
 v{server_version}
 </div> </nav>
 {/* Subtil baggrundstekstur — fixed, ligger bag alt */}
 <div style={{ position:"fixed", top:0, left:76, right:0, bottom:0, backgroundImage:`url(${LOGO_SRC})`, backgroundSize:"400px", backgroundPosition:"center", backgroundRepeat:"no-repeat", opacity:0.03, pointerEvents:"none", zIndex:0 }} />
 {/* Animerede ambient orbs — 3D dybde-effekt */}
 <div style={{ position:"fixed", top:"8%", right:"12%", width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle, rgba(63,212,199,0.04) 0%, transparent 70%)", pointerEvents:"none", zIndex:0, animation:"float 8s ease-in-out infinite", filter:"blur(1px)" }} /> <div style={{ position:"fixed", bottom:"15%", left:"18%", width:320, height:320, borderRadius:"50%", background:"radial-gradient(circle, rgba(79,70,229,0.05) 0%, transparent 70%)", pointerEvents:"none", zIndex:0, animation:"float 11s ease-in-out infinite reverse", filter:"blur(1px)" }} /> <div style={{ position:"fixed", top:"50%", right:"30%", width:200, height:200, borderRadius:"50%", background:"radial-gradient(circle, rgba(167,139,250,0.03) 0%, transparent 70%)", pointerEvents:"none", zIndex:0, animation:"float 14s ease-in-out infinite", filter:"blur(2px)" }} />
 {/* ── TOPBAR — Sprint H: flyttet UD af sv-main, fast, upåvirket af calm-filter ── */}
 <div className="sv-topbar" style={{
 padding:"18px clamp(20px, 4vw, 40px)", display:"flex", alignItems:"center", justifyContent:"space-between",
 position:"fixed", top:0, left:76, right:0, zIndex: T.z_nav,
 minHeight: 80, boxSizing:"border-box",
 background: "linear-gradient(180deg, rgba(5,8,24,0.88) 0%, rgba(8,11,28,0.82) 100%)",
 backdropFilter:"blur(32px) saturate(1.6)", WebkitBackdropFilter:"blur(32px) saturate(1.6)",
 borderBottom:"1px solid rgba(63,212,199,0.06)",
 boxShadow:"0 1px 0 rgba(63,212,199,0.04), 0 8px 32px rgba(0,0,0,0.2)",
 }}> <div>
 {visning === "menu" ? (
 <> <div className="sv-topbar-greeting" style={{ fontSize:22, fontWeight:300, color:"#e2e8f0", letterSpacing:"-0.02em", lineHeight:1.3 }}>
 {(() => { const h = new Date().getHours(); return h>=6&&h<10?"Godmorgen":h>=10&&h<18?"God dag":h>=18&&h<23?"God aften":"God nat"; })()},&nbsp;<span style={{ fontWeight:700, background:"linear-gradient(90deg, #3fd4c7, #a78bfa, #60a5fa)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>{bruger?.navn?.split(" ")[0] || "René"}</span> </div> <div className="sv-topbar-subtitle" style={{ fontSize:12, color:"rgba(255,255,255,0.4)", fontWeight:400, marginTop:4 }}>
 {new Date().toLocaleDateString("da-DK",{weekday:"long",day:"numeric",month:"long"})} · <span style={{ color:"rgba(255,255,255,0.6)" }}>{MINE_SAGER.length} aktive sager</span> </div> </>
 ) : (
 <> <div className="sv-topbar-page-title" style={{ fontSize:20, fontWeight:400, color:"#e2e8f0", display:"flex", alignItems:"center", gap:10 }}> <span style={{ fontSize:22 }}>{side_meta.ikon}</span> <span>{side_meta.titel}</span> </div> </>
 )}
 </div> <div style={{ display:"flex", gap:8, alignItems:"center" }}> {/* H2-fix: sv-dk-alle-sager-knap → CSS hæver font-size til 13px i mørkt tema */}
 <button className="sv-topbar-skjul-mobil sv-dk-alle-sager-knap" onClick={()=>naviger("klienter")} style={{
 background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"10px 16px",
 color:"#b0bec5", fontSize:13, fontWeight:500, fontFamily:"'Inter',system-ui,sans-serif",
 cursor:"pointer", transition:"all 0.2s",
 }}
 onMouseOver={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.2)";e.currentTarget.style.color="#e2e8f0"}}
 onMouseOut={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.1)";e.currentTarget.style.color="#94a3b8"}}>
 Alle sager
 </button> <button onClick={()=>naviger("ny-klient")} style={{
 background:"linear-gradient(135deg, #3fd4c7 0%, #2ca89f 100%)",
 border:"none", borderRadius:9, padding:"10px 20px",
 color:"#04150f", fontSize:13, fontWeight:700, fontFamily:"'Inter',system-ui,sans-serif",
 cursor:"pointer", transition:"all 0.2s",
 boxShadow:"0 4px 14px rgba(63,212,199,0.25)",
 }}
 onMouseOver={e=>{ e.currentTarget.style.boxShadow="0 6px 24px rgba(63,212,199,0.4)"; e.currentTarget.style.transform="translateY(-1px)"; }}
 onMouseOut={e=>{ e.currentTarget.style.boxShadow="0 4px 14px rgba(63,212,199,0.25)"; e.currentTarget.style.transform="translateY(0)"; }}>
 ＋ Ny klient
 </button> <div className="sv-topbar-theme"><ThemeToggle /></div>
 {/* Notifikations-klokke */}
 <div style={{ position:"relative" }}>
   <button onClick={() => setVisNotifPanel(v => !v)} title="Notifikationer" aria-label="Notifikationer"
     style={{ width:44, height:44, borderRadius:8, background: vis_notif_panel ? "rgba(63,212,199,0.12)" : "rgba(255,255,255,0.04)", border: vis_notif_panel ? "1px solid rgba(63,212,199,0.35)" : "1px solid rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.7)", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative" }}>
     🔔
     {ulæste_antal > 0 && <span style={{ position:"absolute", top:6, right:6, background:"#fb7185", borderRadius:"50%", minWidth:16, height:16, fontSize:10, fontWeight:700, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px", lineHeight:1, pointerEvents:"none" }}>{ulæste_antal > 99 ? "99+" : ulæste_antal}</span>}
   </button>
   {/* FEJL-004-fix: renderes via portal til document.body, så panelet undgår at blive
       beskåret af .sv-topbar's overflow:hidden (sat i utils.js for mobil-bredder —
       topbaren har der fast height/max-height:56px, som klipper alt position:fixed-
       indhold der visuelt rækker udenfor, selvom z-index og position ellers er korrekte). */}
   {vis_notif_panel && createPortal(
     <div style={{ position:"fixed", top:70, right:16, width:340, maxHeight:480, background:"rgba(10,14,46,0.98)", border:"1px solid rgba(63,212,199,0.2)", borderRadius:12, boxShadow:"0 16px 48px rgba(0,0,0,0.6)", zIndex:99999, display:"flex", flexDirection:"column", overflow:"hidden" }}
       onClick={e => e.stopPropagation()}>
       <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
         <span style={{ fontSize:14, fontWeight:700, color:"#f1f5f9" }}>Notifikationer {ulæste_antal > 0 && <span style={{ background:"#fb7185", borderRadius:10, padding:"1px 6px", fontSize:11, color:"#fff" }}>{ulæste_antal}</span>}</span>
         <button onClick={marker_alle_laest} style={{ fontSize:11, color:"#3fd4c7", background:"none", border:"none", cursor:"pointer", padding:"2px 6px" }}>Markér alle læst</button>
       </div>
       <div style={{ overflowY:"auto", flex:1 }}>
         {notif_liste.filter(n => n.db_id).length === 0 ? (
           <div style={{ padding:"24px 16px", textAlign:"center", color:"#64748b", fontSize:13 }}>Ingen ulæste notifikationer</div>
         ) : notif_liste.filter(n => n.db_id).map(n => (
           <div key={n.id} style={{ padding:"12px 16px", borderBottom:"1px solid rgba(255,255,255,0.04)", display:"flex", alignItems:"flex-start", gap:10 }}>
             <span style={{ fontSize:18, flexShrink:0 }}>{n.type === 'fil_uploadet' ? '📎' : n.type === 'fil_redigeret' ? '✏️' : n.type === 'haendelse_oprettet' ? '📋' : '🔔'}</span>
             <div style={{ flex:1, minWidth:0 }}>
               <div style={{ fontSize:13, color:"#e2e8f0", lineHeight:1.4 }}>{n.besked || n.titel}</div>
               <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{n.frist ? new Date(n.frist).toLocaleString('da-DK', { dateStyle:'short', timeStyle:'short' }) : ''}</div>
             </div>
             <button onClick={() => marker_laest(n.db_id)} style={{ fontSize:11, color:"#3fd4c7", background:"none", border:"none", cursor:"pointer", flexShrink:0, padding:"2px" }} title="Markér læst">✓</button>
           </div>
         ))}
       </div>
     </div>,
     document.body
   )}
 </div>
 {/* Live status dot */}
 <div className="sv-topbar-live" style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 10px", background:"rgba(52,211,153,0.06)", border:"1px solid rgba(52,211,153,0.15)", borderRadius:20 }}> <div className="sv-status-dot-live" style={{ width:7, height:7, borderRadius:"50%", background:"#34d399", flexShrink:0 }} /> <span style={{ fontSize:10, fontWeight:700, color:"#34d399", letterSpacing:"0.12em", fontFamily:"'Inter',system-ui,sans-serif" }}>LIVE</span> </div> {/* #7: Log ud — min 44×44 tap-target + aria-label */}
 <button className="sv-topbar-logout" onClick={log_ud} title="Log ud" aria-label="Log ud" style={{
 width:44, height:44, borderRadius:8,
 background:"rgba(255,255,255,0.04)", border:T.skillelinje,
 color:"rgba(255,255,255,0.4)", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center",
 cursor:"pointer",
 }}>Log ud</button> </div> </div>
 {/* ═══ HOVEDINDHOLD — Sprint H: topbar flyttet ud, filter kan påvirke dette uden at bryde fixed-positioning ═══ */}
 {/* U1 WCAG: skip-link til hovedindhold for tastatur/screen-reader-brugere */}
 <a href="#sv-hovedindhold" className="sv-skip-link">Spring til hovedindhold</a> <div id="sv-hovedindhold" role="main" tabIndex="-1" className="sv-main sv-main-offset" style={{ marginLeft:72, flex:1, minHeight:"100vh", position:"relative", paddingTop: 80, boxSizing:"border-box" }}>
 {/* ── UNDERSIDER (renderes i stedet for dashboard) ── */}
 {visning==="søg" && (
 <SubBoundary navn="Søgning"><SøgVisning onTilbage={()=>naviger("menu")}
 onÅbnSag={(sagsnummer, klient_id) => { if (sagsnummer) { const sag = mine_sager.find(s => s.sagsnummer === sagsnummer); if (sag) setÅbnSagId(sag.id); } naviger("klienter"); }} /></SubBoundary>
 )}
 {visning==="min-profil" && <SubBoundary navn="Min profil"><BrugerProfil bruger={bruger} onTilbage={()=>setVisning("menu")} /></SubBoundary>}
 {visning==="klienter" && <SubBoundary navn="Sager"><KlientOversigt onTilbage={()=>naviger("menu")} åbn_sag_id={åbn_sag_id} onÅbnet={()=>setÅbnSagId(null)} maal_haendelse_id={maal_haendelse_id} onMaalAabnet={()=>setMaalHaendelseId(null)} fjern_nye_haendelse={fjern_nye_haendelse} /></SubBoundary>}
 {visning==="upload-godkend" && <SubBoundary navn="Afventer godkendelse"><UploadAfventerView onTilbage={()=>naviger("menu")} bruger={bruger} /></SubBoundary>}
 {visning==="ny-klient" && <SubBoundary navn="Ny klient"><NyKlient onFærdig={(klient_id)=>naviger("klienter")} onAnnuller={()=>naviger("menu")} /></SubBoundary>}
 {visning==="import" && <SubBoundary navn="Import"><ImportWizard onFærdig={()=>naviger("menu")} onAnnuller={()=>naviger("menu")} kørImport={kør_import} /></SubBoundary>}
 {visning==="hjaelp" && <SubBoundary navn="Hjælp"><HjaelpManualVisning /></SubBoundary>}
 {false && (
 <div style={{ padding:"clamp(16px, 3vw, 28px) clamp(16px, 4vw, 40px)", maxWidth:900, margin:"0 auto", position:"relative", zIndex:1, animation:"fadeUp 0.3s ease" }}>
 {[
 { ikon:"", titel:"Sager", emner:[
 ["Opret ny sag","Gå til 'Ny klient' i sidebaren → udfyld oplysninger → vælg sagstype → tryk 'Opret'"],
 ["Find en sag","Gå til 'Sager' i sidebaren → brug søgefeltet eller filtrér på status"],
 ["Sæt deadline","Åbn sagen → tryk på 'Næste deadline' feltet eller brug deadline-sektionen"],
 ["Skift sagsbehandler","Åbn sagen → fanen 'Sagsbehandlere' → vælg ny primær/sekundær"],
 ["Luk en sag","Åbn sagen → tryk 'Luk sag' knappen i bunden"],
 ]},
 { ikon:"", titel:"Import", emner:[
 ["Importér sagsmappe","Gå til 'Import' i sidebaren → vælg mappe fra PC → AI analyserer → bekræft og importér"],
 ["Hvad sker der under import?","Filerne uploades til OneDrive i de rigtige undermapper. En klient og sag oprettes automatisk. Du bliver sagsbehandler."],
 ["Filnavne med æøå","Specialtegn understøttes. Filer navngives automatisk baseret på AI-analyse."],
 ]},
 { ikon:"", titel:"Deadlines", emner:[
 ["Opret deadline","Åbn sagen → 'Deadlines' sektionen → tryk '+ Ny deadline'"],
 ["Slet deadline","Åbn deadline-dialogen → sæt dato til tom → gem"],
 ["Farver","Rød = overskredet, Gul = i dag eller snart, Grøn = mere end 7 dage, Grå = ingen deadline"],
 ["Notifikationer","Du får automatisk besked når en deadline nærmer sig eller overskrives"],
 ]},
 { ikon:"", titel:"Søgning", emner:[
 ["Fritekstsøgning","Gå til 'Søg' → skriv søgeord → resultater vises for klienter, sager, hændelser og filer"],
 ["AI-søgning","I søgesiden kan du stille spørgsmål på dansk — AI gennemsøger alle sagsakter"],
 ]},
 { ikon:"", titel:"Filer og dokumenter", emner:[
 ["Upload filer","Åbn sagen → fanen 'Sagsakter' → tryk 'Upload' eller træk filer ind"],
 ["Se filer","Klik på et dokument for at åbne preview (PDF, billeder, video, lyd)"],
 ["Filer organiseres","Filer placeres automatisk i OneDrive-undermapper baseret på type"],
 ]},
 { ikon:"", titel:"Din profil", emner:[
 ["Skift adgangskode","Tryk på dit profil-ikon → fanen 'Adgangskode' → udfyld nuværende og ny kode"],
 ["Rediger navn/email","Tryk på dit profil-ikon → ændr felterne → tryk 'Gem'"],
 ]},
 { ikon:"", titel:"Tips", emner:[
 ["Keyboard genveje","Escape = tilbage til overblik, Alt+← = tilbage"],
 ["Notifikationer","Vigtige beskeder vises i toppen. Klik for at åbne den relevante sag."],
 ["Problemer?","Kontakt René Sørensen (direktør) eller skriv til kontakt@skilsmissevejen.dk"],
 ]},
 ].map(sektion => (
 <div key={sektion.titel} style={{ position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.03)", border:T.kort_kant, borderRadius:14, padding:"18px 22px", marginBottom:12 }}> <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${T.turkis}, ${T.turkis}44, transparent)`, borderRadius:"14px 14px 0 0", pointerEvents:"none" }} /> <div style={{ fontSize:14, fontWeight:600, color:T.turkis, marginBottom:12, display:"flex", alignItems:"center", gap:8 }}> <span style={{ fontSize:18 }}>{sektion.ikon}</span> {sektion.titel}
 </div>
 {sektion.emner.map(([spg, svar], i) => (
 <div key={i} style={{ padding:"10px 0", borderBottom: i < sektion.emner.length-1 ? T.skillelinje : "none" }}> <div style={{ fontSize:13, fontWeight:600, color:T.creme, marginBottom:4 }}>{spg}</div> <div style={{ fontSize:13, color:T.dæmpet, lineHeight:1.5 }}>{svar}</div> </div>
 ))}
 </div>
 ))}
 </div>
 )}
 {visning==="indstillinger" && <SubBoundary navn="Admin-kontrolpanel"><AdminKontrolpanel onTilbage={()=>naviger("menu")} /></SubBoundary>}
 {visning==="dashboard" && <SubBoundary navn="Dashboard"><DashboardVisning onTilbage={()=>naviger("menu")} onÅbnSag={(sag_id)=>{ setÅbnSagId(sag_id); naviger("klienter"); }} /></SubBoundary>}
 {visning==="import-onedrive" && <SubBoundary navn="Import fra OneDrive"><ImportFraOneDrive onFærdig={()=>{ hent_mine_sager(); naviger("menu"); }} onAnnuller={()=>naviger("menu")} /></SubBoundary>}
 {visning==="geo" && <SubBoundary navn="Geografisk overblik"><DanmarkskortVisning onTilbage={()=>naviger("menu")} /></SubBoundary>}
 {visning==="teamnoter" && <SubBoundary navn="Teamnotater"><OSTeamNoter /></SubBoundary>}
 {visning==="filtjek" && <SubBoundary navn="Filtjek"><FiltjekOprydning onÅbnHaendelse={(sag_id, haendelse_id) => { setÅbnSagId(sag_id); setMaalHaendelseId(haendelse_id); naviger("klienter"); }} /></SubBoundary>}
 {visning==="kurser" && <SubBoundary navn="Kurser"><KursusHub bruger={bruger} onValgKursus={(id)=>naviger(`kurser-${id}`)} /></SubBoundary>}
 {visning==="kurser-foraeldreansvarsloven" && <SubBoundary navn="Forældreansvarsloven"><KursusForaeldreansvarsloven bruger={bruger} onTilbage={()=>naviger("kurser")} /></SubBoundary>}
 {visning==="kurser-barnetslov" && <SubBoundary navn="Barnets lov"><KursusBarnetslov bruger={bruger} onTilbage={()=>naviger("kurser")} /></SubBoundary>}
 {visning==="os" && bruger?.os_admin && (
 <div style={{ position:"fixed", inset:0, zIndex:200, background:"linear-gradient(135deg, #060917 0%, #0a0f22 50%, #070e1c 100%)", overflow:"auto" }}> <SubBoundary navn="OS Dashboard"><OSDashboardNy onTilbage={()=>naviger("menu")} bruger={bruger} onLogout={log_ud} /></SubBoundary> </div>
 )}
 {/* ── DASHBOARD (kun på overblik) ── */}
 {visning==="menu" && (
 <div style={{ padding:"clamp(16px, 3vw, 28px) clamp(16px, 4vw, 40px)", maxWidth:1200, margin:"0 auto", position:"relative", zIndex:1 }}>
 {/* ── KPI BOKSE — glassmorphism premium ── */}
 <div className="sv-kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:14, marginBottom:24 }}>
 {[
 { id:"kritisk", label:"Kritiske sager", tal:kritiske.length, ikon:"", farve:"#ff6b6b", sub: kritiske.length>0 ? kritiske.slice(0,2).map(s=>s.klient.split(",")[0]).join(", ") : "Ingen overskredne", urgent:kritiske.length>0 },
 { id:"deadline", label:"Deadlines i dag", tal:dl_i_dag.length, ikon:"", farve:"#ffd43b", sub: dl_i_dag.length>0 ? dl_i_dag.slice(0,2).map(s=>s.klient.split(",")[0]).join(", ") : "Ingen i dag", urgent:dl_i_dag.length>0 },
 { id:"aktiv", label:"Mine sager", tal:MINE_SAGER.length, ikon:"", farve:"#3fd4c7", sub:`${total_sager||mine_sager.length} på hele kontoret`, urgent:false },
 { id:"opgaver", label:"Deadlines total", tal:dl_alle.length, ikon:"", farve:"#818cf8", sub: dl_alle.length>0 ? "Sager med deadlines" : "Ingen deadlines", urgent:false },
 ].map(k=>{
 const aktiv = aktiv_filter===k.id;
 return (
 <div key={k.id} role="button" tabIndex={0}
 aria-label={k.label + ": " + k.tal + (aktiv ? " — filter aktivt" : "")}
 onClick={()=>setAktivFilter(aktiv?null:k.id)}
 onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),setAktivFilter(aktiv?null:k.id))}
 className={`sv-kpi-kort sv-3d-tilt sv-holo-card sv-card-pop`}
 style={{
 position:"relative", overflow:"hidden",
 background: aktiv ? `linear-gradient(135deg, ${k.farve}15 0%, rgba(8,12,30,0.85) 100%)` : "rgba(8,12,30,0.7)",
 border: aktiv ? `1px solid ${k.farve}44` : `1px solid ${k.farve}14`,
 borderRadius:16, padding:"20px 22px 18px", cursor:"pointer",
 backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
 boxShadow: aktiv ? `0 8px 32px ${k.farve}22, inset 0 1px 0 rgba(255,255,255,0.08), 0 0 0 0.5px ${k.farve}18` : `0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px ${k.farve}0a`,
 /* RENE-2: deadline-pulse kun på avatar-niveau — fjernes fra hele kortet */
 animation: "none",
 transition:"transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s, background 0.2s, border-color 0.2s",
 }}
 onMouseOver={e=>{
 if(!aktiv) e.currentTarget.style.background="rgba(255,255,255,0.07)";
 e.currentTarget.style.boxShadow=`0 12px 40px ${k.farve}18, inset 0 1px 0 rgba(255,255,255,0.1)`;
 }}
 onMouseOut={e=>{
 if(!aktiv) e.currentTarget.style.background="rgba(255,255,255,0.04)";
 e.currentTarget.style.boxShadow=aktiv?`0 8px 32px ${k.farve}20, inset 0 1px 0 rgba(255,255,255,0.08)`:"0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)";
 }}>
 {/* Top accent strip */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${k.farve}, ${k.farve}44, transparent)`, borderRadius:"16px 16px 0 0" }} />
 {/* Ambient glow orb */}
 <div style={{ position:"absolute", top:-10, right:-10, width:70, height:70, borderRadius:"50%", background:`${k.farve}10`, filter:"blur(18px)", pointerEvents:"none" }} />
 {/* Header row */}
 <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}> <span aria-hidden="true" style={{ fontSize:26, lineHeight:1 }}>{k.ikon}</span>
 {aktiv && <span style={{ fontSize:10, color:k.farve, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:700, background:`${k.farve}15`, border:`1px solid ${k.farve}30`, borderRadius:10, padding:"2px 7px" }}>Filtreret</span>}
 </div>
 {/* Big number */}
 <div style={{ fontSize:44, fontWeight:800, color:k.farve, lineHeight:1, marginBottom:6, letterSpacing:"-0.03em", fontFamily:"'Inter',system-ui,sans-serif", animation:"number-count 0.4s ease", textShadow:`0 0 20px ${k.farve}44, 0 0 40px ${k.farve}22` }}>
 {k.tal}
 </div>
 {/* Label */}
 <div style={{ fontSize:12, color:aktiv ? k.farve+"cc" : "#94a3b8", letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:600, marginBottom:k.sub ? 8 : 0 }}>{k.label}</div>
 {k.sub && <div style={{ fontSize:12, color:"#64748b", lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.sub}</div>}
 </div>
 );
 })}
 </div>
 {/* ── AKTIVITETSGRAF — 14 dage ── */}
 {/* #16: Tom-tilstand for aktivitetsgraf */}
 {!aktiv_filter && aktivitet_data.length === 0 && (
 <div style={{ position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.03)", border:T.kort_kant, borderRadius:16, padding:"24px 20px", marginBottom:16, textAlign:"center" }}> <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg, #3fd4c7, #3fd4c744, transparent)", borderRadius:"16px 16px 0 0" }} /> <div style={{ fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", color:"#b0bec5", fontWeight:600, marginBottom:8 }}>Aktivitet — seneste 14 dage</div> <div style={{ color:"#475569", fontSize:13 }}>Ingen aktivitet i perioden</div> </div>
 )}
 {!aktiv_filter && aktivitet_data.length > 0 && (
 <div className="sv-3d-tilt" style={{ position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.03)", border:T.kort_kant, borderRadius:16, padding:"16px 20px 8px", marginBottom:16, backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)", boxShadow:"0 2px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
 {/* Accent strip */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg, #3fd4c7, #3fd4c744, transparent)", borderRadius:"16px 16px 0 0", pointerEvents:"none" }} />
 {/* Glow orb */}
 <div style={{ position:"absolute", top:-20, right:20, width:120, height:120, borderRadius:"50%", background:"rgba(63,212,199,0.06)", filter:"blur(30px)", pointerEvents:"none" }} /> <div style={{ fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", color:"#b0bec5", fontWeight:600, marginBottom:12, position:"relative" }}>Aktivitet — seneste 14 dage</div> <ResponsiveContainer width="100%" height={120}> <AreaChart data={aktivitet_data} margin={{ top: 4, right: 4, left: 8, bottom: 0 }}> <defs> <linearGradient id="aktivitet_grad" x1="0" y1="0" x2="0" y2="1"> <stop offset="0%" stopColor="#3fd4c7" stopOpacity={0.3} /> <stop offset="100%" stopColor="#3fd4c7" stopOpacity={0} /> </linearGradient> </defs> {/* #11: Y-akse med labels så grafen er læsbar */}
 <XAxis dataKey="label" tick={{ fontSize:10, fill:"#64748b" }} axisLine={false} tickLine={false} interval={1} /> <YAxis allowDecimals={false} tick={{ fontSize:10, fill:"#64748b" }} axisLine={false} tickLine={false} width={24} /> <Tooltip
 contentStyle={{ background:"#0f1328", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, fontSize:12, color:"#e2e8f0" }}
 labelStyle={{ color:"#b0bec5", marginBottom:4 }}
 formatter={(v) => [v + " hændelser", ""]}
 /> <Area type="monotone" dataKey="antal" stroke="#3fd4c7" strokeWidth={2} fill="url(#aktivitet_grad)" dot={false} activeDot={{ r:4, fill:"#3fd4c7", stroke:"#0a0e2e", strokeWidth:2 }} /> </AreaChart> </ResponsiveContainer> </div>
 )}
 {/* ── DEADLINES I DAG + MØDER (vises kun når intet filter er aktivt) ── */}
 {!aktiv_filter && (
 <div className="sv-deadline-moeder-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:16 }}>
 {/* Deadlines denne uge */}
 <div className="sv-3d-tilt" style={{ position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)", padding:"16px 18px", boxShadow:"0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)" }}>
 {/* Accent strip */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg, #ffd43b, #ffd43b44, transparent)", borderRadius:"16px 16px 0 0" }} />
 {/* Glow */}
 <div style={{ position:"absolute", top:-15, right:-15, width:80, height:80, borderRadius:"50%", background:"rgba(255,212,59,0.08)", filter:"blur(20px)", pointerEvents:"none" }} /> <div style={{ fontSize:12, color:T.dæmpet, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}> <span> Deadlines denne uge</span> {/* H4-fix: sv-dk-se-alle → CSS hæver font-size til 12px i mørkt tema */}
 <span className="sv-dk-se-alle" style={{ color:T.turkis, cursor:"pointer", fontSize:11, background:"rgba(63,212,199,0.1)", border:"1px solid rgba(63,212,199,0.2)", borderRadius:8, padding:"2px 8px" }} onClick={()=>setAktivFilter("deadline")}>Se alle →</span> </div>
 {DEADLINES_UGE.length === 0 ? (
 <div style={{ textAlign:"center", padding:"16px 0", color:"#475569", fontSize:13 }}>Ingen deadlines denne uge </div>
 ) : (
 <div className="sv-widget-scroll" style={{ maxHeight:260, overflowY:"auto" }}>
 {DEADLINES_UGE.map((s,i,arr)=>{
 const dl_dage = s.deadline ? Math.ceil((new Date(s.deadline) - new Date()) / 86400000) : 99;
 const dl_farve = dl_dage <= 0 ? "#ff6b6b" : dl_dage <= 7 ? "#ffd43b" : "#69db7c";
 const dl_kritisk = dl_dage <= 0;
 const initials = (s.klient||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
 return (
 <div key={s.id} role="button" tabIndex={0}
 style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 8px", borderBottom:i<arr.length-1?"1px solid rgba(255,255,255,0.05)":"none", cursor:"pointer", borderRadius:8, transition:"background 0.15s" }}
 onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
 onMouseOut={e=>e.currentTarget.style.background="transparent"}
 onClick={()=>setDashboardDeadlineDialog({ sag_id:s.id, ny_deadline:s.deadline?.split("T")[0]||"", ny_titel:s.deadline_titel||"", original_titel:s.deadline_titel||"", original_deadline:s.deadline_txt||"", begrundelse:"", eksisterende:true })}
 onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),setDashboardDeadlineDialog({ sag_id:s.id, ny_deadline:s.deadline?.split("T")[0]||"", ny_titel:s.deadline_titel||"", original_titel:s.deadline_titel||"", original_deadline:s.deadline_txt||"", begrundelse:"", eksisterende:true }))}> <div aria-label={dl_dage<=0?"Overskredet":dl_dage<=7?"Snart deadline":"Ok"} style={{ width:30, height:30, borderRadius:"50%", background:`${dl_farve}18`, border:`1px solid ${dl_farve}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:dl_farve, flexShrink:0, animation: dl_kritisk ? "deadline-pulse 2.5s ease-in-out infinite" : "none" }}>{initials}</div> <div style={{ flex:1, minWidth:0 }}> <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.klient}</div> <div className="sv-liste-sagsnr" style={{ fontSize:11, color:"#64748b" }}>{s.sagsnummer}</div> </div> {/* RENE-4: max-width reduceret så klientnavn har plads */}
 <div className="sv-widget-dl-box" style={{ textAlign:"right", flexShrink:0, background:`${dl_farve}12`, border:`1px solid ${dl_farve}30`, borderRadius:8, padding:"4px 7px", maxWidth:"clamp(72px, 28%, 110px)" }}> <div style={{ fontSize:11, fontWeight:700, color:dl_farve, lineHeight:1.2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.deadline_txt}</div>
 <div className="sv-widget-dl-sub" style={{ fontSize:9, color: s.deadline_titel ? "#94a3b8" : "#fb7185", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.deadline_titel || "⚠ Mangler beskrivelse"}</div>
 </div> </div>
 );
 })}
 </div>
 )}
 </div>
 {/* Møder de næste 7 dage */}
 <KommendeMoederOverblik onÅbnSag={(sag_id) => { setÅbnSagId(sag_id); naviger("klienter"); }} eget_navn={bruger?.navn} />
 {/* Sager med nye hændelser */}
 <div className="sv-3d-tilt" style={{ position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)", padding:"16px 18px", boxShadow:"0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)" }}>
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg, #38bdf8, #38bdf844, transparent)", borderRadius:"16px 16px 0 0" }} />
 <div style={{ position:"absolute", top:-15, right:-15, width:80, height:80, borderRadius:"50%", background:"rgba(56,189,248,0.08)", filter:"blur(20px)", pointerEvents:"none" }} /> <div style={{ fontSize:12, color:T.dæmpet, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}> <span> Nye hændelser {nye_haendelser_liste.length > 0 && `(${nye_haendelser_liste.length})`}</span>
 {NYE_HAENDELSER_SAGER.length > 0 && (
 <span className="sv-dk-se-alle" style={{ color:T.turkis, cursor:"pointer", fontSize:11, background:"rgba(63,212,199,0.1)", border:"1px solid rgba(63,212,199,0.2)", borderRadius:8, padding:"2px 8px" }} onClick={()=>setAktivFilter("nye_haendelser")}>Se alle →</span>
 )} </div>
 {nye_haendelser_liste.length === 0 ? (
 <div style={{ textAlign:"center", padding:"16px 0", color:"#475569", fontSize:13 }}>Ingen usete hændelser</div>
 ) : (
 <div className="sv-widget-scroll" style={{ maxHeight:260, overflowY:"auto" }}>
 {nye_haendelser_liste.map((h,i,arr)=>{
 const gaa_til_haendelse = () => { setÅbnSagId(h.sag_id); setMaalHaendelseId(h.id); naviger("klienter"); };
 return (
 <div key={h.id} role="button" tabIndex={0}
 style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 8px", borderBottom:i<arr.length-1?"1px solid rgba(255,255,255,0.05)":"none", cursor:"pointer", borderRadius:8, transition:"background 0.15s" }}
 onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
 onMouseOut={e=>e.currentTarget.style.background="transparent"}
 onClick={gaa_til_haendelse}
 onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&(e.preventDefault(),gaa_til_haendelse())}> <div style={{ position:"relative", width:30, height:30, borderRadius:"50%", background:"#38bdf818", border:"1px solid #38bdf844", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#38bdf8", flexShrink:0, marginTop:1 }}>
 {(h.klient_navn||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
 <span style={{ position:"absolute", top:-2, right:-2, width:9, height:9, borderRadius:"50%", background:"#38bdf8", border:"2px solid rgba(8,12,30,0.9)", animation:"nyt-pulse 2s ease-in-out infinite" }} />
 </div> <div style={{ flex:1, minWidth:0 }}> <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{h.klient_navn} <span style={{ fontWeight:400, color:"#64748b" }}>· {h.sagsnummer}</span></div> <div style={{ fontSize:12, color:"#94a3b8", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginTop:1 }}>{h.titel}</div> </div> </div>
 );
 })}
 </div>
 )} </div>
 </div>
 )}
 {/* ── NYLIGT SET — hurtig adgang til sidst åbnede sager ── */}
 {!aktiv_filter && recentlyViewed.length > 0 && (
 <div style={{ position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.03)", border:T.kort_kant, borderRadius:14, padding:"14px 18px", marginBottom:16, backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)" }}> <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg, #818cf8, #818cf844, transparent)", borderRadius:"14px 14px 0 0" }} /> <div style={{ fontSize:12, color:T.dæmpet, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10, fontWeight:600 }}> Nyligt set</div> <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
 {recentlyViewed.map(s => (
 <button key={s.id}
 onClick={()=>{ setÅbnSagId(s.id); naviger("klienter"); }}
 style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 12px", background:"rgba(129,140,248,0.08)", border:"1px solid rgba(129,140,248,0.2)", borderRadius:20, cursor:"pointer", color:T.creme, fontSize:12, fontFamily:"'Inter',system-ui,sans-serif", transition:"all 0.15s" }}
 onMouseOver={e=>{ e.currentTarget.style.background="rgba(129,140,248,0.18)"; e.currentTarget.style.borderColor="rgba(129,140,248,0.45)"; }}
 onMouseOut={e=>{ e.currentTarget.style.background="rgba(129,140,248,0.08)"; e.currentTarget.style.borderColor="rgba(129,140,248,0.2)"; }}> <span style={{ color:"#818cf8", fontWeight:700, fontSize:11 }}>{s.sagsnummer}</span> <span style={{ color:"#475569" }}>·</span> <span style={{ maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:"#cbd5e1" }}>{(s.klient||"").split(",")[0]}</span> </button>
 ))}
 </div> </div>
 )}
 {/* ── SAGSLISTE — premium med liste/kort toggle ── */}
 <div style={{ position:"relative", background:"rgba(255,255,255,0.03)", border:T.kort_kant, borderRadius:18, overflow:"hidden", marginBottom:16, backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)", boxShadow:"0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
 {/* Accent strip */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${T.turkis}, ${T.turkis}44, transparent)`, borderRadius:"18px 18px 0 0", pointerEvents:"none", zIndex:1 }} /> <div className="sv-liste-filterbar" style={{ padding:"14px 20px", borderBottom:T.skillelinje, display:"flex", justifyContent:"space-between", alignItems:"center", position:"relative", flexWrap:"wrap", gap:8, overflowX:"auto" }}> <div style={{ display:"flex", alignItems:"center", gap:10 }}> <span style={{ fontSize:12, color: aktiv_filter ? T.turkis : "#94a3b8", letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600 }}>
 {aktiv_filter === "alle_kontor" ? "Alle sager" : aktiv_filter ? filter_titler[aktiv_filter] : "Mine sager"}
 </span> <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>({filtrerede_sager.length})</span>
 {aktiv_filter && <button onClick={()=>setAktivFilter(null)} style={{ fontSize:12, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:4, padding:"2px 10px", color:"#b0bec5", cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}>× Nulstil</button>}
 </div> <div className="sv-liste-filterbar-right" style={{ display:"flex", alignItems:"center", gap:8 }}>
 {/* Sortering */}
 <Select value={sag_sortering} onChange={e=>setSagSortering(e.target.value)}
 style={{ padding:"5px 12px", borderRadius:6, width:"auto", colorScheme:"dark", fontSize:13 }}> <option value="deadline">Deadline</option> <option value="alfa_az">Alfabet A–Å</option> <option value="alfa_za">Alfabet Å–A</option> <option value="aktivitet">Sidste aktivitet</option> <option value="risiko">Sagsstyrke</option> </Select>
 {aktiv_filter !== "alle_kontor" && (
 <button onClick={()=>setAktivFilter("alle_kontor")} style={{ fontSize:12, background:"rgba(255,255,255,0.04)", border:T.skillelinje, borderRadius:6, padding:"4px 12px", color:"#b0bec5", cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}>
 Alle →
 </button>
 )}
 {/* Liste/kort toggle */}
 <div style={{ display:"flex", background:"rgba(255,255,255,0.04)", borderRadius:6, border:T.skillelinje, overflow:"hidden" }}> <button onClick={()=>{setSagVisning("liste");localStorage.setItem("sv_sag_visning","liste")}}
 style={{ padding:"4px 10px", background:sag_visning==="liste"?"rgba(63,212,199,0.15)":"transparent", border:"none", color:sag_visning==="liste"?T.turkis:"#64748b", fontSize:14, cursor:"pointer", lineHeight:1 }}
 title="Listevisning"></button> <button onClick={()=>{setSagVisning("kort");localStorage.setItem("sv_sag_visning","kort")}}
 style={{ padding:"4px 10px", background:sag_visning==="kort"?"rgba(63,212,199,0.15)":"transparent", border:"none", color:sag_visning==="kort"?T.turkis:"#64748b", fontSize:14, cursor:"pointer", lineHeight:1 }}
 title="Kortvisning">▦</button> </div> </div> </div>
 {filtrerede_sager.length === 0 ? (
 <div style={{ padding:"40px", textAlign:"center" }}> <div style={{ fontSize:28, marginBottom:10 }}></div> <div style={{ fontSize:14, color:"#cbd5e1", marginBottom:6 }}>Ingen sager matcher filteret</div> <div style={{ fontSize:12, color:"#b0bec5", marginBottom:16 }}>Prøv et andet filter eller vis alle sager</div>
 {aktiv_filter && (
 <button onClick={() => setAktivFilter(null)} style={{ fontSize:13, background:"rgba(79,70,229,0.15)", border:"1px solid rgba(79,70,229,0.3)", borderRadius:8, padding:"8px 20px", color:"#a5b4fc", cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif", transition:"background 0.2s" }}>
 × Nulstil filter
 </button>
 )}
 </div>
 ) : (() => {
 // Sortering
 const sorteret = [...filtrerede_sager].sort((a,b) => {
 if (sag_sortering === "alfa_az") return (a.klient||"").localeCompare(b.klient||"", "da");
 if (sag_sortering === "alfa_za") return (b.klient||"").localeCompare(a.klient||"", "da");
 if (sag_sortering === "risiko") return (b.risiko||0) - (a.risiko||0);
 if (sag_sortering === "aktivitet") return (b.haendelser||0) - (a.haendelser||0);
 // Default: deadline (sager med deadline først, derefter dato)
 if (!a.deadline && !b.deadline) return 0;
 if (!a.deadline) return 1;
 if (!b.deadline) return -1;
 return new Date(a.deadline) - new Date(b.deadline);
 });
 const vis_sager = sorteret;
 const primære = vis_sager.filter(s => s.primær === bruger_navn);
 const sekundære = vis_sager.filter(s => s.sekundær === bruger_navn);
 const status_farve = (s) => STATUS_FARVE2[s]||"#64748b";
 // ── LISTE-VISNING ──
 const render_liste = (sag, i, arr) => {
 const akc = deadline_kant_farve(sag);
 const initials = (sag.klient||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
 const er_prim = sag.primær === bruger_navn;
 return (
 <div key={sag.id}
 className="sv-liste-row"
 onClick={()=>{ setÅbnSagId(sag.id); naviger("klienter"); }}
 style={{
 position:"relative", overflow:"hidden",
 padding:"14px 20px",
 borderBottom: i<arr.length-1 ? "1px solid rgba(255,255,255,0.05)" : "none",
 display:"flex", alignItems:"center", gap:14,
 cursor:"pointer",
 transition:"background 0.15s, transform 0.15s",
 animation: er_kritisk(sag) ? "deadline-pulse 2.5s ease-in-out infinite" : "none",
 }}
 onMouseOver={e=>{ e.currentTarget.style.background="rgba(255,255,255,0.04)"; e.currentTarget.style.transform="translateX(2px)"; }}
 onMouseOut={e=>{ e.currentTarget.style.background="transparent"; e.currentTarget.style.transform="translateX(0)"; }}>
 {/* Gradient accent bar left */}
 <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:`linear-gradient(180deg, ${akc}, ${akc}44)`, borderRadius:"0 2px 2px 0" }} />
 {/* Avatar */}
 <div style={{
 position:"relative", width:36, height:36, borderRadius:"50%", flexShrink:0,
 background:`linear-gradient(135deg, ${akc}22, ${akc}44)`,
 border:`1.5px solid ${akc}55`,
 display:"flex", alignItems:"center", justifyContent:"center",
 fontSize:12, fontWeight:700, color:akc, letterSpacing:"0.05em",
 }}>{initials}
 {sag.har_nye_haendelser && (
 <span title="Nye hændelser siden sidste besøg" style={{
 position:"absolute", top:-2, right:-2, width:12, height:12, borderRadius:"50%",
 background:"#38bdf8", border:"2px solid rgba(8,12,30,0.9)",
 animation:"nyt-pulse 2s ease-in-out infinite",
 }} />
 )}
 </div>
 {/* Main info */}
 <div style={{ flex:1, minWidth:0 }}> <div className="sv-liste-badge-row" style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2, flexWrap:"wrap" }}> <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0, flex:1 }}> <div style={{ fontSize:15, fontWeight:600, color:"#f1f5f9", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 }}>{sag.klient}</div>
 {sag.har_nye_haendelser && (
 <span title="Nye hændelser siden sidste besøg" style={{
 fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.06em",
 padding:"2px 8px", borderRadius:20, flexShrink:0,
 color:"#38bdf8", background:"#0a1420",
 border:"1px solid #38bdf8aa", boxShadow:"0 0 8px rgba(56,189,248,0.4)" }}>
 Ny
 </span>
 )} </div> {notif_sag_ids.has(sag.id) && <span title="Ulæste notifikationer på denne sag" style={{ flexShrink:0, background:"#fb7185", borderRadius:"50%", width:8, height:8, display:"inline-block" }} />} <span title={er_prim ? "Du er primær sagsbehandler" : "Du er sekundær sagsbehandler"} style={{ fontSize:11, letterSpacing:"0.08em", textTransform:"uppercase", padding:"2px 7px", borderRadius:10, flexShrink:0,
 color: er_prim ? T.turkis : "#60a5fa",
 background: er_prim ? "rgba(63,212,199,0.12)" : "rgba(96,165,250,0.12)",
 border:`1px solid ${er_prim ? "rgba(63,212,199,0.25)" : "rgba(96,165,250,0.25)"}`
 }}>{er_prim ? "PRIMÆR" : "SEK"}</span> <span className="sv-liste-status-badge" style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em", padding:"2px 7px", borderRadius:10, flexShrink:0,
 color:status_farve(sag.status), background:`${status_farve(sag.status)}15`, border:`1px solid ${status_farve(sag.status)}30`}}>
 {sag.status}
 </span> </div> <div className="sv-liste-sagsnr" style={{ fontSize:12, color:"#7c8ea0" }}>{sag.sagsnummer} · {sagstype_label(sag.type)}</div>
 {/* Risk bar */}
 <div className="sv-skjul-mobil" title="Sagsstyrke: AI-beregnet score 0–100 — Stærk ≥70, Moderat ≥40, Svag <40" style={{ display:"flex", alignItems:"center", gap:6, marginTop:6 }}> <div style={{ flex:1, maxWidth:120, height:3, borderRadius:2, background:"rgba(255,255,255,0.06)", overflow:"hidden" }}> <div style={{ height:"100%", width:`${Math.max(sag.risiko,3)}%`, background:`linear-gradient(90deg, ${sag.styrke_farve||"#ff6b6b"}, ${sag.styrke_farve||"#ff6b6b"}88)`, borderRadius:2, transition:"width 0.5s ease" }} /> </div> <span style={{ fontSize:11, color:sag.styrke_farve||"#ff6b6b", fontWeight:600 }}>{sag.risiko >= 70 ? "Stærk" : sag.risiko >= 40 ? "Moderat" : "Svag"}</span> </div> </div>
 {/* Deadline box */}
 <div className="sv-liste-deadline" style={{ flexShrink:0, textAlign:"right" }}>
 {sag.deadline ? (
 <div
 onClickCapture={e=>{ e.stopPropagation(); e.preventDefault(); setDashboardDeadlineDialog({ sag_id:sag.id, ny_deadline:sag.deadline?.split("T")[0]||"", ny_titel:sag.deadline_titel||"", original_titel:sag.deadline_titel||"", original_deadline:sag.deadline_txt||"", begrundelse:"", eksisterende:true }); }}
 title="Klik for at ændre deadline"
 style={{ background:`${akc}15`, border:`1px solid ${akc}33`, borderRadius:8, padding:"5px 10px", cursor:"pointer", transition:"background 0.15s" }}
 onMouseOver={e=>{e.stopPropagation();e.currentTarget.style.background=`${akc}25`}}
 onMouseOut={e=>e.currentTarget.style.background=`${akc}15`}> <div style={{ fontSize:13, fontWeight:700, color:akc, lineHeight:1.2 }}>{sag.deadline_txt}</div> <div title={sag.deadline_titel || "Mangler beskrivelse"} style={{ fontSize:11, color: sag.deadline_titel ? "#94a3b8" : "#fb7185", marginTop:2, maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sag.deadline_titel || "⚠ Mangler"}</div> </div>
 ) : (
 <div onClick={e=>{ e.stopPropagation(); setDashboardDeadlineDialog({ sag_id:sag.id, ny_deadline:"", ny_titel:"", begrundelse:"" }); }}
 style={{ fontSize:12, color:"#475569", cursor:"pointer", padding:"5px 10px", borderRadius:8, border:"1px dashed rgba(255,255,255,0.08)", transition:"all 0.15s" }}
 onMouseOver={e=>{e.currentTarget.style.borderColor="rgba(63,212,199,0.3)";e.currentTarget.style.color="#94a3b8"}}
 onMouseOut={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";e.currentTarget.style.color="#475569"}}
 title="Klik for at sætte deadline">+ Sæt deadline</div>
 )}
 </div> </div>
 );
 };
 // ── KORT-VISNING ──
 const render_kort = (sag) => {
 const akc = deadline_kant_farve(sag);
 const initials = (sag.klient||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
 const er_prim = sag.primær === bruger_navn;
 return (
 <div key={sag.id}
 onClick={()=>{ setÅbnSagId(sag.id); naviger("klienter"); }}
 style={{
 position:"relative", overflow:"hidden",
 background:"rgba(255,255,255,0.04)",
 border:"1px solid rgba(255,255,255,0.08)",
 borderRadius:16, padding:"20px 20px 16px",
 cursor:"pointer",
 backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
 transition:"transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s, background 0.2s",
 animation: er_kritisk(sag) ? "deadline-pulse 2.5s ease-in-out infinite" : "none",
 }}
 onMouseOver={e=>{
 e.currentTarget.style.transform="translateY(-4px) scale(1.01)";
 e.currentTarget.style.boxShadow=`0 16px 40px rgba(0,0,0,0.4), 0 0 0 1px ${akc}44`;
 e.currentTarget.style.background="rgba(255,255,255,0.07)";
 }}
 onMouseOut={e=>{
 e.currentTarget.style.transform="translateY(0) scale(1)";
 e.currentTarget.style.boxShadow="none";
 e.currentTarget.style.background="rgba(255,255,255,0.04)";
 }}>
 {/* Accent strip top */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${akc}, ${akc}44, transparent)`, borderRadius:"16px 16px 0 0" }} /> <div style={{ position:"absolute", top:-20, right:-20, width:80, height:80, borderRadius:"50%", background:`${akc}10`, filter:"blur(20px)", pointerEvents:"none" }} />
 {/* Top row: avatar + name + role */}
 <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}> <div style={{ position:"relative", width:40, height:40, borderRadius:"50%", flexShrink:0,
 background:`linear-gradient(135deg, ${akc}22, ${akc}44)`,
 border:`1.5px solid ${akc}55`,
 display:"flex", alignItems:"center", justifyContent:"center",
 fontSize:14, fontWeight:700, color:akc, letterSpacing:"0.05em" }}>
 {initials}
 {sag.har_nye_haendelser && (
 <span title="Nye hændelser siden sidste besøg" style={{
 position:"absolute", top:-3, right:-3, width:14, height:14, borderRadius:"50%",
 background:"#38bdf8", border:"2px solid rgba(8,12,30,0.9)",
 animation:"nyt-pulse 2s ease-in-out infinite",
 }} />
 )}
 </div> <div style={{ flex:1, minWidth:0 }}> <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}> <div style={{ fontSize:15, fontWeight:700, color:"#f1f5f9", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.3, minWidth:0 }}>{sag.klient}</div>
 {sag.har_nye_haendelser && (
 <span title="Nye hændelser siden sidste besøg" style={{
 fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.06em",
 padding:"2px 8px", borderRadius:20, flexShrink:0,
 color:"#38bdf8", background:"#0a1420",
 border:"1px solid #38bdf8aa", boxShadow:"0 0 8px rgba(56,189,248,0.4)" }}>
 Ny
 </span>
 )} </div> <div style={{ fontSize:11, color:"#64748b", marginTop:1 }}>{sag.sagsnummer}</div> </div>
 <span title={er_prim ? "Du er primær sagsbehandler" : "Du er sekundær sagsbehandler"} style={{ fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", padding:"3px 8px", borderRadius:20, flexShrink:0,
 color: er_prim ? T.turkis : "#60a5fa",
 background: er_prim ? "rgba(63,212,199,0.12)" : "rgba(96,165,250,0.12)",
 border:`1px solid ${er_prim ? "rgba(63,212,199,0.25)" : "rgba(96,165,250,0.25)"}`
 }}>{er_prim ? "PRIMÆR" : "SEK"}</span> </div>
 {/* Metadata */}
 <div style={{ fontSize:12, color:"#64748b", marginBottom:12, paddingLeft:52 }}>
 {sagstype_label(sag.type)}
 <span style={{ marginLeft:8, fontSize:10, textTransform:"uppercase", letterSpacing:"0.06em",
 padding:"2px 7px", borderRadius:20,
 color:status_farve(sag.status), background:`${status_farve(sag.status)}12`, border:`1px solid ${status_farve(sag.status)}25` }}>
 {sag.status}
 </span> </div>
 {/* Deadline */}
 <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:"8px 12px", marginBottom:12 }}>
 {sag.deadline ? (
 <div
 onClickCapture={e=>{ e.stopPropagation(); e.preventDefault(); setDashboardDeadlineDialog({ sag_id:sag.id, ny_deadline:sag.deadline?.split("T")[0]||"", ny_titel:sag.deadline_titel||"", original_titel:sag.deadline_titel||"", original_deadline:sag.deadline_txt||"", begrundelse:"", eksisterende:true }); }}
 style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}> <span style={{ fontSize:13, color:akc }}></span> <div> <div style={{ fontSize:13, fontWeight:700, color:akc, lineHeight:1.2 }}>{sag.deadline_txt}</div>
 <div style={{ fontSize:11, color: sag.deadline_titel ? "#94a3b8" : "#fb7185" }}>{sag.deadline_titel || "⚠ Mangler beskrivelse"}</div>
 </div> </div>
 ) : (
 <div onClick={e=>{ e.stopPropagation(); setDashboardDeadlineDialog({ sag_id:sag.id, ny_deadline:"", ny_titel:"", begrundelse:"" }); }}
 style={{ fontSize:12, color:"#475569", fontStyle:"italic", cursor:"pointer" }}>+ Sæt deadline</div>
 )}
 </div>
 {/* Risk bar */}
 <div title="Sagsstyrke: AI-beregnet score 0–100 — Stærk ≥70, Moderat ≥40, Svag <40" style={{ display:"flex", alignItems:"center", gap:8 }}> <div style={{ fontSize:11, color:"#475569", width:48, flexShrink:0 }}>Styrke</div> <div style={{ flex:1, height:5, borderRadius:3, background:"rgba(255,255,255,0.05)", overflow:"hidden" }}> <div style={{ height:"100%", width:`${Math.max(sag.risiko||0, 3)}%`, background:`linear-gradient(90deg, ${sag.styrke_farve||"#ff6b6b"}, ${sag.styrke_farve||"#ff6b6b"}88)`, borderRadius:3, transition:"width 0.6s ease" }} /> </div> <span style={{ fontSize:11, color:sag.styrke_farve||"#ff6b6b", fontWeight:700, width:28, textAlign:"right", flexShrink:0 }}>{sag.risiko}</span> </div> </div>
 );
 };
 // Sekundær separator
 const separator = (
 <div style={{ padding:"10px 20px", background:"rgba(96,165,250,0.06)", borderTop:"1px solid rgba(96,165,250,0.15)", borderBottom:"1px solid rgba(96,165,250,0.15)" }}> <div style={{ fontSize:12, color:"#60a5fa", letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:600 }}>Sekundære sager</div> </div>
 );
 if (sag_visning === "kort") {
 if (!aktiv_filter && primære.length > 0 && sekundære.length > 0) {
 return (
 <div style={{ padding:16 }}> <div className="sv-sag-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(340px, 1fr))", gap:14 }}>
 {primære.map(s => render_kort(s))}
 </div> <div style={{ padding:"12px 0", margin:"16px 0" }}> <div style={{ fontSize:12, color:"#60a5fa", letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:600, paddingBottom:8, borderBottom:"1px solid rgba(96,165,250,0.15)" }}>Sekundære sager</div> </div> <div className="sv-sag-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(340px, 1fr))", gap:14 }}>
 {sekundære.map(s => render_kort(s))}
 </div> </div>
 );
 }
 return (
 <div style={{ padding:16 }}> <div className="sv-sag-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(340px, 1fr))", gap:14 }}>
 {vis_sager.map(s => render_kort(s))}
 </div> </div>
 );
 }
 // Liste med primær/sekundær separator
 if (!aktiv_filter && primære.length > 0 && sekundære.length > 0) {
 return (
 <>
 {primære.map((s,i,arr) => render_liste(s, i, arr))}
 {separator}
 {sekundære.map((s,i,arr) => render_liste(s, i, arr))}
 </>
 );
 }
 return vis_sager.map((s,i,arr) => render_liste(s, i, arr));
 })()}
 </div>
 {/* Hurtige links er nu i topbar */}
 </div>
 )}
 </div> </div> <Toast /> <SvHjaelp bruger={bruger} /> </>
 );
}
// ── CSS Injection